import { Briefcase, Github, Globe, Linkedin, MapPin, Plane } from "lucide-react";
import { CAREER_LEVELS, WORK_MODELS } from "@/components/profile/options";
import { stateName } from "@/lib/profileSearch";
import { BrandLogo } from "@/components/BrandLogo";

const label = (opts, v) => opts.find((o) => o.value === v)?.label || v;

const Chips = ({ title, items, gold }) =>
  items?.length ? (
    <div>
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((s) => <span key={s} className={gold ? "rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-xs text-[#F3C649]" : "rounded-full border border-[#3F3F46] bg-[#18181B] px-3 py-1 text-xs text-zinc-300"}>{s}</span>)}
      </div>
    </div>
  ) : null;

const LinkBtn = ({ href, icon: Icon, text }) =>
  href ? (
    <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#3F3F46] px-4 py-2 text-xs text-zinc-200 transition-colors hover:border-[#D4AF37] hover:text-[#F3C649]" data-testid={`public-link-${text.toLowerCase()}`}>
      <Icon className="h-3.5 w-3.5" />{text}
    </a>
  ) : null;

export const PublicCard = ({ d }) => {
  const role = [d.current_title, d.current_employer].filter(Boolean).join(" @ ");
  const where = [...new Set([d.location, ...(d.preferred_states || []).filter((s) => s !== "ANY" && !(d.location || "").toUpperCase().includes(s)).map(stateName)].filter(Boolean))];
  const meta = [d.years_experience != null && `${d.years_experience}+ yrs`, d.career_level && label(CAREER_LEVELS, d.career_level), ...(d.work_models || []).map((w) => label(WORK_MODELS, w))].filter(Boolean);
  return (
    <article className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-[#0f0f12]" data-testid="public-card">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-[90px]" />
      <div className="relative p-7 md:p-10">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
          {d.picture ? <img src={d.picture} alt="" className="h-20 w-20 rounded-2xl border border-[#D4AF37]/50 object-cover" />
            : <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-[#D4AF37]/50 bg-[#D4AF37]/10 font-display text-3xl text-[#D4AF37]">{(d.name || "?")[0]}</div>}
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-100 sm:text-4xl" data-testid="public-name">{d.name}</h1>
            {d.headline && <p className="mt-1 text-base text-[#F3C649]">{d.headline}</p>}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
              {role && <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{role}</span>}
              {where.length > 0 && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{where.slice(0, 3).join(" · ")}</span>}
              {d.open_to_relocation && <span className="flex items-center gap-1 text-emerald-400"><Plane className="h-3 w-3" />Open to relocate</span>}
            </div>
          </div>
        </div>
        {meta.length > 0 && <div className="mt-6 flex flex-wrap gap-2">{meta.map((m) => <span key={m} className="rounded-md bg-[#27272A] px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-zinc-300">{m}</span>)}</div>}
        {d.bio && <p className="mt-6 max-w-2xl whitespace-pre-wrap text-sm leading-relaxed text-zinc-300 md:text-base">{d.bio}</p>}
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <Chips title="Looking for" items={d.target_roles} gold />
          <Chips title="Skills" items={d.skills} />
          <Chips title="Tech stack" items={d.tech_stack} />
          <Chips title="Strengths" items={d.soft_skills} />
          <Chips title="Certifications" items={d.certifications} />
        </div>
        <div className="mt-8 flex flex-wrap gap-2">
          <LinkBtn href={d.linkedin_url} icon={Linkedin} text="LinkedIn" />
          <LinkBtn href={d.github_url} icon={Github} text="GitHub" />
          <LinkBtn href={d.portfolio_url} icon={Globe} text="Website" />
        </div>
        <div className="mt-10 flex items-center justify-between border-t border-[#27272A] pt-5">
          <BrandLogo size="sm" tagline={false} className="opacity-90" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">Verified RPOPRO profile</span>
        </div>
      </div>
    </article>
  );
};
